import { type FormEvent, useEffect, useState } from "react";
import type { DobPrecision } from "@/shared/types/patient";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  GuardianRelationship,
  PatientCreateRequest,
  PatientDuplicateWarning,
  PatientGender,
  PatientResponse,
} from "../../../shared/types/patient";
import { patientsApi } from "../../../shared/api/patients";
import { apiError } from "../../../shared/api/client";
import { AddressPicker } from "../../../shared/ui/AddressPicker";
import { hisIntegrationApi, hisIntegrationQueryKeys } from "../../../shared/api/hisIntegration";
import { DuplicateWarningCard } from "./DuplicateWarningCard";
import {
  bloodTypeLabel,
  genderLabel,
  guardianRelationshipLabel,
} from "../../../shared/format";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

type Mode = "create" | "edit";
type FieldErrors = Partial<Record<string, string>>;

interface Props {
  mode: Mode;
  initial?: PatientResponse;
  /** Where to navigate on success — usually back to the detail page. */
  onSuccessRedirect: (id: string) => string;
}

const SELECT_CLASS =
  "flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50";

const EMPTY: PatientCreateRequest = {
  fullName: "",
  gender: "UNKNOWN",
  dateOfBirth: null,
  dobPrecision: "DAY",
  phone: "",
  email: "",
  addressLine: "",
  wardCode: null,
  provinceCode: null,
  nationality: "VN",
  ethnicityCode: "",
  occupation: "",
  organization: "",
  bloodType: null,
  heightCm: null,
  weightKg: null,
  notes: "",
  guardianName: "",
  guardianPhone: "",
  guardianRelationship: null,
  primaryBranchId: null,
  primaryDoctorId: null,
  cccd: "",
  cmnd: "",
  bhyt: "",
  forceDuplicate: false,
};

function fromInitial(p: PatientResponse): PatientCreateRequest {
  return {
    ...EMPTY,
    fullName: p.fullName,
    gender: p.gender ?? "UNKNOWN",
    dateOfBirth: p.dateOfBirth,
    dobPrecision: p.dobPrecision,
    phone: p.phoneE164 ?? "",
    email: p.email ?? "",
    addressLine: p.addressLine ?? "",
    wardCode: p.wardCode ?? null,
    provinceCode: p.provinceCode ?? null,
    nationality: p.nationality,
    ethnicityCode: p.ethnicityCode ?? "",
    occupation: p.occupation ?? "",
    organization: p.organization ?? "",
    bloodType: p.bloodType,
    heightCm: p.heightCm,
    weightKg: p.weightKg,
    notes: p.notes ?? "",
    guardianName: p.guardianName ?? "",
    guardianPhone: p.guardianPhone ?? "",
    guardianRelationship: p.guardianRelationship,
    primaryBranchId: p.primaryBranchId,
    primaryDoctorId: p.primaryDoctorId,
  };
}

export function PatientForm({ mode, initial, onSuccessRedirect }: Props) {
  const navigate = useNavigate();
  const [data, setData] = useState<PatientCreateRequest>(() =>
    initial ? fromInitial(initial) : EMPTY
  );
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [warning, setWarning] = useState<PatientDuplicateWarning | null>(null);
  const [forceDuplicate, setForceDuplicate] = useState(false);

  // ADR-0020: a tenant bound to an external HIS uses Addy only for booking, so its patients'
  // address vocabulary is that HIS's own province/ward dictionary, not Addy Geo — see AddressPicker.
  const bindingQuery = useQuery({
    queryKey: hisIntegrationQueryKeys.binding(),
    queryFn: () => hisIntegrationApi.get(),
    staleTime: 5 * 60 * 1000,
  });
  const addressSource = bindingQuery.data && bindingQuery.data.hisType !== "NATIVE" ? "his" : "geo";

  useEffect(() => {
    if (initial) setData(fromInitial(initial));
  }, [initial]);

  const createMutation = useMutation({
    mutationFn: (req: PatientCreateRequest) => patientsApi.create(req),
    onSuccess: (res) => {
      if (res.warning && res.warning.confidence !== "STRONG") {
        // Soft warning: create succeeded, surface it but still navigate.
        setWarning(res.warning);
      }
      navigate(onSuccessRedirect(res.patient.id));
    },
    onError: (e) => {
      const err = apiError(e);
      if (err.code === "patient.duplicate" && err.problem) {
        const w = (err.problem as { warning?: PatientDuplicateWarning }).warning;
        if (w) {
          setWarning(w);
          setFormError(null);
          return;
        }
      }
      setFormError(err.detail);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (req: PatientCreateRequest) =>
      patientsApi.update(initial!.id, {
        fullName: req.fullName,
        gender: req.gender,
        dateOfBirth: req.dateOfBirth,
        dobPrecision: req.dobPrecision,
        phone: req.phone,
        email: req.email,
        addressLine: req.addressLine,
        wardCode: req.wardCode,
        provinceCode: req.provinceCode,
        nationality: req.nationality,
        ethnicityCode: req.ethnicityCode,
        occupation: req.occupation,
        organization: req.organization,
        bloodType: req.bloodType,
        heightCm: req.heightCm,
        weightKg: req.weightKg,
        notes: req.notes,
        guardianName: req.guardianName,
        guardianPhone: req.guardianPhone,
        guardianRelationship: req.guardianRelationship,
        primaryBranchId: req.primaryBranchId,
        primaryDoctorId: req.primaryDoctorId,
      }),
    onSuccess: (res) => navigate(onSuccessRedirect(res.id)),
    onError: (e) => setFormError(apiError(e).detail),
  });

  const busy = createMutation.isPending || updateMutation.isPending;

  function validate(): FieldErrors {
    const next: FieldErrors = {};
    if (!data.fullName.trim()) next.fullName = "Họ và tên không được để trống";
    else if (data.fullName.length > 256) next.fullName = "Tối đa 256 ký tự";

    if (data.phone && !/^(\+?84|0)\d{9,10}$/.test(data.phone)) {
      next.phone = "Số điện thoại không hợp lệ";
    }
    if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      next.email = "Email không hợp lệ";
    }
    if (data.cccd && !/^\d{12}$/.test(data.cccd)) {
      next.cccd = "CCCD phải có 12 chữ số";
    }
    if (data.cmnd && !/^\d{9}$/.test(data.cmnd)) {
      next.cmnd = "CMND phải có 9 chữ số";
    }
    if (data.dateOfBirth && new Date(data.dateOfBirth) >= new Date()) {
      next.dateOfBirth = "Ngày sinh phải trong quá khứ";
    }
    if (data.heightCm != null && (data.heightCm <= 0 || data.heightCm > 300)) {
      next.heightCm = "Chiều cao 0–300 cm";
    }
    if (data.weightKg != null && (data.weightKg <= 0 || data.weightKg > 500)) {
      next.weightKg = "Cân nặng 0–500 kg";
    }
    return next;
  }

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFormError(null);
    const v = validate();
    setErrors(v);
    if (Object.keys(v).length > 0) return;

    if (mode === "create") {
      createMutation.mutate({ ...data, forceDuplicate });
    } else {
      updateMutation.mutate(data);
    }
  }

  /**
   * Keep what the user already typed when the precision changes (#18856). The stored value stays
   * a full ISO date because the column is a date; the day/month parts are placeholders that
   * `dob_precision` marks as unknown.
   */
  function reshapeDob(dob: string | null | undefined, next: DobPrecision): string | null {
    if (!dob) return null;
    if (next === "YEAR") return `${dob.slice(0, 4)}-01-01`;
    if (next === "MONTH") return `${dob.slice(0, 7)}-01`;
    return dob;
  }

  function bind<K extends keyof PatientCreateRequest>(field: K) {
    return {
      value: ((data[field] as unknown) ?? "") as string,
      onChange: (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
      ) => {
        const raw = e.target.value;
        let value: unknown = raw === "" ? null : raw;
        // numeric fields
        if (field === "heightCm" || field === "weightKg") {
          value = raw === "" ? null : Number(raw);
        }
        setData((d) => ({ ...d, [field]: value as PatientCreateRequest[K] }));
      },
      disabled: busy,
    };
  }

  return (
    <form className="space-y-6 font-sans" onSubmit={onSubmit} noValidate>
      {warning && (
        <DuplicateWarningCard
          warning={warning}
          onAcceptAndContinue={
            mode === "create"
              ? () => {
                  setForceDuplicate(true);
                  createMutation.mutate({ ...data, forceDuplicate: true });
                }
              : undefined
          }
          busy={busy}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle id="sec-identity" className="text-base">
            Thông tin cá nhân
          </CardTitle>
        </CardHeader>
        <CardContent
          aria-labelledby="sec-identity"
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
        >
          <div className="space-y-1.5">
            <Label htmlFor="fullName">Họ và tên *</Label>
            <Input
              id="fullName"
              type="text"
              autoFocus
              aria-invalid={Boolean(errors.fullName)}
              {...bind("fullName")}
            />
            {errors.fullName && (
              <p className="text-sm text-destructive">{errors.fullName}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="gender">Giới tính</Label>
            <select id="gender" className={SELECT_CLASS} {...bind("gender")}>
              {(Object.keys(genderLabel) as PatientGender[]).map((g) => (
                <option key={g} value={g}>
                  {genderLabel[g]}
                </option>
              ))}
            </select>
          </div>
          {/*
            #18856 — the precision comes FIRST and decides what the date field is.
            It used to be a plain `type="date"` beside a precision dropdown, so recording "we only
            know the year" still meant typing a full date, and the precision was almost always left
            at DAY. On real data that showed up as 19% of patients born exactly on 01/01, every one
            of them flagged as exact. Asking for only what is known removes the choice to lie.
            The stored date keeps a placeholder day; PatientAge is what stops it being read as one.
          */}
          <div className="space-y-1.5">
            <Label htmlFor="dobPrecision">Biết ngày sinh tới đâu?</Label>
            <select
              id="dobPrecision"
              className={SELECT_CLASS}
              value={data.dobPrecision ?? "DAY"}
              disabled={busy}
              onChange={(e) => {
                const next = e.target.value as DobPrecision;
                setData((d) => ({
                  ...d,
                  dobPrecision: next,
                  // Re-shape what is already typed rather than clearing it: switching from
                  // "đầy đủ" to "chỉ năm" should keep the year, not make the user retype.
                  dateOfBirth: reshapeDob(d.dateOfBirth, next),
                }));
              }}
            >
              <option value="DAY">Đầy đủ (ngày/tháng/năm)</option>
              <option value="MONTH">Chỉ tháng/năm</option>
              <option value="YEAR">Chỉ năm</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="dob">
              {data.dobPrecision === "YEAR"
                ? "Năm sinh"
                : data.dobPrecision === "MONTH"
                  ? "Tháng/năm sinh"
                  : "Ngày sinh"}
            </Label>
            {data.dobPrecision === "YEAR" ? (
              <Input
                id="dob"
                type="number"
                inputMode="numeric"
                min={1900}
                max={2100}
                placeholder="VD: 1958"
                aria-invalid={Boolean(errors.dateOfBirth)}
                value={data.dateOfBirth ? data.dateOfBirth.slice(0, 4) : ""}
                onChange={(e) => {
                  const y = e.target.value.slice(0, 4);
                  setData((d) => ({
                    ...d,
                    dateOfBirth: /^\d{4}$/.test(y) ? `${y}-01-01` : null,
                  }));
                }}
                disabled={busy}
              />
            ) : (
              <Input
                id="dob"
                type={data.dobPrecision === "MONTH" ? "month" : "date"}
                aria-invalid={Boolean(errors.dateOfBirth)}
                value={
                  data.dobPrecision === "MONTH"
                    ? (data.dateOfBirth ?? "").slice(0, 7)
                    : (data.dateOfBirth ?? "")
                }
                onChange={(e) => {
                  const v = e.target.value;
                  setData((d) => ({
                    ...d,
                    dateOfBirth: !v
                      ? null
                      : d.dobPrecision === "MONTH"
                        ? `${v}-01`
                        : v,
                  }));
                }}
                disabled={busy}
              />
            )}
            {data.dobPrecision !== "DAY" && (
              <p className="text-xs text-muted-foreground">
                Hệ thống ghi nhận đây là ngày sinh <strong>không đầy đủ</strong> và sẽ không coi nó
                là chính xác khi tính tuổi.
              </p>
            )}
            {errors.dateOfBirth && (
              <p className="text-sm text-destructive">{errors.dateOfBirth}</p>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle id="sec-contact" className="text-base">
            Liên hệ
          </CardTitle>
        </CardHeader>
        <CardContent aria-labelledby="sec-contact" className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="phone">Số điện thoại</Label>
              <Input
                id="phone"
                type="tel"
                inputMode="tel"
                placeholder="0967369125"
                aria-invalid={Boolean(errors.phone)}
                {...bind("phone")}
              />
              {errors.phone && (
                <p className="text-sm text-destructive">{errors.phone}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="bệnh nhân@example.vn"
                aria-invalid={Boolean(errors.email)}
                {...bind("email")}
              />
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="addressLine">Địa chỉ</Label>
              <Input
                id="addressLine"
                type="text"
                placeholder="Số nhà, đường, khu phố"
                {...bind("addressLine")}
              />
            </div>
          </div>
          <div>
            <AddressPicker
              provinceCode={data.provinceCode ?? null}
              wardCode={data.wardCode ?? null}
              disabled={busy}
              source={addressSource}
              onChange={({ provinceCode, wardCode }) =>
                setData((d) => ({ ...d, provinceCode, wardCode }))
              }
            />
          </div>
        </CardContent>
      </Card>

      {mode === "create" && (
        <Card>
          <CardHeader>
            <CardTitle id="sec-id" className="text-base">
              Định danh
            </CardTitle>
          </CardHeader>
          <CardContent
            aria-labelledby="sec-id"
            className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
          >
            <div className="space-y-1.5">
              <Label htmlFor="cccd">CCCD (12 chữ số)</Label>
              <Input
                id="cccd"
                type="text"
                inputMode="numeric"
                maxLength={12}
                aria-invalid={Boolean(errors.cccd)}
                {...bind("cccd")}
              />
              {errors.cccd && (
                <p className="text-sm text-destructive">{errors.cccd}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cmnd">CMND (cũ, 9 chữ số)</Label>
              <Input
                id="cmnd"
                type="text"
                inputMode="numeric"
                maxLength={9}
                aria-invalid={Boolean(errors.cmnd)}
                {...bind("cmnd")}
              />
              {errors.cmnd && (
                <p className="text-sm text-destructive">{errors.cmnd}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bhyt">Số thẻ BHYT</Label>
              <Input id="bhyt" type="text" maxLength={20} {...bind("bhyt")} />
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle id="sec-demo" className="text-base">
            Nhân khẩu học
          </CardTitle>
        </CardHeader>
        <CardContent
          aria-labelledby="sec-demo"
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
        >
          <div className="space-y-1.5">
            <Label htmlFor="nationality">Quốc tịch</Label>
            <Input
              id="nationality"
              type="text"
              maxLength={8}
              {...bind("nationality")}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="occupation">Nghề nghiệp</Label>
            <Input
              id="occupation"
              type="text"
              maxLength={120}
              {...bind("occupation")}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="organization">Đơn vị công tác</Label>
            <Input
              id="organization"
              type="text"
              maxLength={200}
              {...bind("organization")}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="bloodType">Nhóm máu</Label>
            <select id="bloodType" className={SELECT_CLASS} {...bind("bloodType")}>
              <option value="">Không rõ</option>
              {Object.entries(bloodTypeLabel).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="heightCm">Chiều cao (cm)</Label>
            <Input
              id="heightCm"
              type="number"
              step="0.1"
              min="0"
              max="300"
              aria-invalid={Boolean(errors.heightCm)}
              {...bind("heightCm")}
            />
            {errors.heightCm && (
              <p className="text-sm text-destructive">{errors.heightCm}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="weightKg">Cân nặng (kg)</Label>
            <Input
              id="weightKg"
              type="number"
              step="0.1"
              min="0"
              max="500"
              aria-invalid={Boolean(errors.weightKg)}
              {...bind("weightKg")}
            />
            {errors.weightKg && (
              <p className="text-sm text-destructive">{errors.weightKg}</p>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle id="sec-guardian" className="text-base">
            Người giám hộ / Liên hệ khẩn cấp
          </CardTitle>
        </CardHeader>
        <CardContent
          aria-labelledby="sec-guardian"
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
        >
          <div className="space-y-1.5">
            <Label htmlFor="guardianName">Họ tên người giám hộ</Label>
            <Input
              id="guardianName"
              type="text"
              maxLength={200}
              {...bind("guardianName")}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="guardianPhone">Số điện thoại</Label>
            <Input
              id="guardianPhone"
              type="tel"
              inputMode="tel"
              {...bind("guardianPhone")}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="guardianRelationship">Mối quan hệ</Label>
            <select
              id="guardianRelationship"
              className={SELECT_CLASS}
              {...bind("guardianRelationship")}
            >
              <option value="">—</option>
              {(Object.keys(guardianRelationshipLabel) as GuardianRelationship[]).map(
                (r) => (
                  <option key={r} value={r}>
                    {guardianRelationshipLabel[r]}
                  </option>
                )
              )}
            </select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle id="sec-notes" className="text-base">
            Ghi chú y khoa
          </CardTitle>
        </CardHeader>
        <CardContent aria-labelledby="sec-notes">
          <div className="space-y-1.5">
            <Label htmlFor="notes">Ghi chú</Label>
            <Textarea
              id="notes"
              rows={4}
              maxLength={4000}
              className="resize-y"
              value={data.notes ?? ""}
              onChange={(e) =>
                setData((d) => ({ ...d, notes: e.target.value || null }))
              }
              disabled={busy}
            />
          </div>
        </CardContent>
      </Card>

      {formError && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {formError}
        </div>
      )}

      <div className="flex flex-wrap justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          disabled={busy}
          onClick={() => navigate(-1)}
        >
          Hủy
        </Button>
        <Button type="submit" disabled={busy}>
          {busy
            ? "Đang lưu…"
            : mode === "create"
              ? "Tạo bệnh nhân"
              : "Lưu thay đổi"}
        </Button>
      </div>
    </form>
  );
}
